import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import queryString from 'query-string'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Progress } from '@/components/progress/Progress'
import { getCourseTrackings } from '@/reducers/baltoAcademy/baltoAcademy.actions'

import { formatDateStamp, formatHMS } from './BaltoAcademy.helpers'
import './BaltoAcademyReportingPage.scss'

const columns = [
  { accessor: 'commenced', label: 'Start Date', isSearchable: true },
  { accessor: 'identifier', label: 'User', isSearchable: true },
  { accessor: 'progress_percent', label: 'Progress' },
  { accessor: 'total_time', label: 'Total Time' },
]

const noDataTextFor = (clientId, courseId) => {
  if (!clientId) {
    return 'Select an organization to begin.'
  }
  if (!courseId) {
    return 'Select a course to view users.'
  }
  return 'No user activity for this course.'
}

export const CourseTrackingsTable = ({ courseId, clientId }) => {
  const dispatch = useDispatch()
  const [trackings, setTrackings] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!courseId || !clientId) {
      // Reset loading too, or switching orgs mid-fetch leaves the spinner stuck.
      setTrackings([])
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    dispatch(getCourseTrackings(courseId, clientId))
      .then((data) => {
        if (!cancelled) {
          setTrackings(data)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [courseId, clientId])

  const users = useMemo(
    () =>
      trackings.filter(
        (tracking) => tracking.identifier && String(tracking.clientIdentifier) === String(clientId)
      ),
    [trackings, clientId]
  )

  const rows = useMemo(
    () =>
      users.map((tracking) => {
        const stamp = formatDateStamp(tracking.commenced)
        return {
          index: tracking.id,
          commenced: {
            value: tracking.commenced,
            as: (
              <div className="academy-reporting__stamp">
                <div className="academy-reporting__stamp-date">{stamp.date}</div>
                <div className="academy-reporting__stamp-time">{stamp.time}</div>
              </div>
            ),
          },
          identifier: {
            value: tracking.identifier,
            as: (
              <Link
                className="academy-reporting__course-link"
                to={`/balto-academy/reporting/user?${queryString.stringify({
                  identifier: tracking.identifier,
                  org: clientId,
                })}`}
              >
                {tracking.identifier}
              </Link>
            ),
          },
          progress_percent: {
            value: tracking.progress_percent,
            as: (
              <div className="academy-reporting__progress">
                <Progress
                  className="academy-reporting__progress-bar"
                  value={tracking.progress_percent}
                  success={tracking.progress_percent >= 100 && tracking.passed}
                  critical={tracking.progress_percent >= 100 && !tracking.passed}
                />
                <span className="academy-reporting__progress-pct">
                  {Math.round(tracking.progress_percent || 0)}%
                </span>
              </div>
            ),
          },
          total_time: {
            value: tracking.total_time,
            as: <span className="academy-reporting__mono">{formatHMS(tracking.total_time)}</span>,
          },
        }
      }),
    [users, clientId]
  )

  return (
    <AdvancedTable
      loading={loading}
      rows={rows}
      columns={columns}
      defaultOrderBy="commenced"
      defaultOrder="desc"
      pagination
      rowsPerPage={15}
      index="index"
      itemName="users"
      noDataText={noDataTextFor(clientId, courseId)}
    />
  )
}
